import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, ArrowRight, CheckCircle2, Star, Menu, X, Package, MapPin, FileText, IndianRupee, BarChart3, ShieldCheck, PlayCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { testimonials, featuresIndia } from '../mock/mockData';
import { useState } from 'react';
import { useI18n } from '../lib/i18n';
import LanguageToggle from '../components/LanguageToggle';

const iconMap = { Truck, Package, MapPin, FileText, IndianRupee, BarChart3, ShieldCheck };

export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-xl text-[#0B2545] tracking-tight font-[Poppins]">EZLOAD</span>
            <Badge className="bg-[#FFB800]/15 text-[#B37800] hover:bg-[#FFB800]/15 border-0 ml-1 text-[10px]">INDIA</Badge>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-gray-700 hover:text-[#00A651]">{t('features')}</a>
            <a href="#pricing" className="text-sm font-medium text-gray-700 hover:text-[#00A651]">{t('pricing')}</a>
            <a href="#testimonials" className="text-sm font-medium text-gray-700 hover:text-[#00A651]">{t('customers')}</a>
            <a href="#faq" className="text-sm font-medium text-gray-700 hover:text-[#00A651]">{t('faq')}</a>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login"><Button variant="ghost" className="text-[#0B2545]">{t('signIn')}</Button></Link>
            <Link to="/signup"><Button className="bg-[#00A651] hover:bg-[#008a43] text-white">{t('startFreeTrial')}</Button></Link>
          </div>

          <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-3">
            <a href="#features" className="block text-sm font-medium">Features</a>
            <a href="#pricing" className="block text-sm font-medium">Pricing</a>
            <a href="#testimonials" className="block text-sm font-medium">Customers</a>
            <div className="flex gap-2 pt-2">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full">Sign in</Button></Link>
              <Link to="/signup" className="flex-1"><Button className="w-full bg-[#00A651] hover:bg-[#008a43] text-white">Start Free</Button></Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40"></div>
        <div className="absolute top-20 -right-20 w-96 h-96 bg-[#00A651]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -left-20 w-96 h-96 bg-[#0B2545]/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <Badge className="bg-[#00A651]/10 text-[#00A651] hover:bg-[#00A651]/10 border-0 mb-5 py-1.5 px-3">
              <span className="w-1.5 h-1.5 bg-[#00A651] rounded-full mr-2 animate-pulse"></span>
              {t('tagline')}
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-extrabold text-[#0B2545] leading-[1.1] tracking-tight mb-5 font-[Poppins]">
              {t('heroTitle1')} <span className="text-[#00A651]">{t('heroTitle2')}</span>.
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl">
              {t('heroDesc')}
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <Link to="/signup"><Button size="lg" className="bg-[#00A651] hover:bg-[#008a43] text-white h-12 px-6 text-base">{t('startTrial')} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Button size="lg" variant="outline" className="h-12 px-6 text-base border-[#0B2545]/20 hover:bg-[#0B2545]/5"><PlayCircle className="w-5 h-5 mr-2" />{t('watchDemo')}</Button>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#00A651]" /> {t('noCard')}</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#00A651]" /> {t('gstInvoices')}</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#00A651]" /> {t('upiPayments')}</div>
            </div>
          </div>

          {/* Hero Card Mockup */}
          <div className="relative">
            <div className="bg-white soft-shadow rounded-2xl p-6 border border-gray-100 relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-semibold text-gray-600">LIVE LOAD BOARD</span>
                </div>
                <span className="text-xs text-gray-500">Updated 2 sec ago</span>
              </div>

              {[
                { from: 'Mumbai', to: 'Delhi', mat: 'Textiles', wt: '18T', rate: '₹85,000', truck: '32ft SXL' },
                { from: 'Bengaluru', to: 'Chennai', mat: 'Electronics', wt: '8T', rate: '₹32,000', truck: '20ft Container' },
                { from: 'Ahmedabad', to: 'Pune', mat: 'Cement', wt: '25T', rate: '₹62,000', truck: 'Trailer 40ft' },
              ].map((l, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-3 mb-2 hover:border-[#00A651]/40 hover:bg-[#00A651]/[0.02] transition-colors cursor-pointer">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 font-semibold text-[#0B2545]">
                      <span>{l.from}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-[#00A651]" />
                      <span>{l.to}</span>
                    </div>
                    <span className="font-bold text-[#00A651]">{l.rate}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    <span className="bg-gray-100 px-2 py-0.5 rounded">{l.mat}</span>
                    <span className="bg-gray-100 px-2 py-0.5 rounded">{l.wt}</span>
                    <span className="bg-gray-100 px-2 py-0.5 rounded">{l.truck}</span>
                  </div>
                </div>
              ))}

              <Button className="w-full mt-2 bg-[#0B2545] hover:bg-[#13315C] text-white">View All 12,847 Live Loads <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>

            <div className="absolute -top-6 -right-4 bg-white rounded-xl soft-shadow border border-gray-100 p-3 z-20 animate-truck">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
                  <Truck className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#0B2545]">50,247 Trucks</p>
                  <p className="text-[10px] text-gray-500">Available now</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl soft-shadow border border-gray-100 p-3 z-20">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#FFB800]/20 flex items-center justify-center">
                  <IndianRupee className="w-4 h-4 text-[#B37800]" />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#0B2545]">₹142 Cr</p>
                  <p className="text-[10px] text-gray-500">Settled this month</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-gray-100 bg-[#F7FAF9]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { v: '50,000+', l: 'Verified Truckers' },
            { v: '12,000+', l: 'Active Shippers' },
            { v: '500+', l: 'Cities Covered' },
            { v: '₹142 Cr', l: 'Payments Settled/mo' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-3xl lg:text-4xl font-extrabold text-[#0B2545] font-[Poppins]">{s.v}</p>
              <p className="text-sm text-gray-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 lg:py-28 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Badge className="bg-[#00A651]/10 text-[#00A651] hover:bg-[#00A651]/10 border-0 mb-4">FEATURES</Badge>
          <h2 className="text-3xl lg:text-5xl font-extrabold text-[#0B2545] font-[Poppins] mb-4">Everything you need to run freight in India</h2>
          <p className="text-gray-600 text-lg">From load discovery to e-Way Bill and UPI settlement — built for Indian transporters and shippers.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuresIndia.map((f, i) => {
            const Icon = iconMap[f.icon] || Truck;
            return (
              <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 hover-lift">
                <div className="w-12 h-12 rounded-xl bg-[#00A651]/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#00A651]" strokeWidth={2} />
                </div>
                <h3 className="font-bold text-lg text-[#0B2545] mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-28 bg-[#F7FAF9]">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge className="bg-[#FFB800]/15 text-[#B37800] hover:bg-[#FFB800]/15 border-0 mb-4">PRICING</Badge>
            <h2 className="text-3xl lg:text-5xl font-extrabold text-[#0B2545] font-[Poppins] mb-4">Simple, transparent pricing</h2>
            <p className="text-gray-600 text-lg">Start free for 1 month. No credit card. Cancel anytime.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <p className="text-sm font-semibold text-gray-500">FREE TRIAL</p>
              <div className="mt-2 mb-1 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold text-[#0B2545] font-[Poppins]">₹0</span>
                <span className="text-gray-500">/first month</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">Try everything, risk-free.</p>
              <ul className="space-y-3 mb-8">
                {['Full Load Board access', 'Truck Board & lane match', 'Basic rate insights', 'Up to 10 e-LR/month', 'UPI & COD payments'].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-5 h-5 text-[#00A651] flex-shrink-0 mt-0.5" /><span>{f}</span></li>
                ))}
              </ul>
              <Link to="/signup"><Button className="w-full h-11 bg-[#0B2545] hover:bg-[#13315C] text-white">Start Free Trial</Button></Link>
            </div>

            <div className="bg-[#0B2545] text-white rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#00A651]/30 rounded-full blur-2xl"></div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-[#FFB800]">PRO — MOST POPULAR</p>
                <Badge className="bg-[#00A651] text-white border-0">Recommended</Badge>
              </div>
              <div className="mt-2 mb-1 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold font-[Poppins]">₹500</span>
                <span className="text-white/70">+ GST /month</span>
              </div>
              <p className="text-sm text-white/70 mb-6">₹590 total incl. 18% GST. After trial.</p>
              <ul className="space-y-3 mb-8">
                {['Unlimited Load & Truck Board', 'Advanced lane rate insights', 'Unlimited e-LR & e-Way Bills', 'GST-compliant invoicing', 'UPI, RTGS, NEFT, COD settlements', 'Priority verified partner access', '24x7 phone & WhatsApp support'].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-5 h-5 text-[#00A651] flex-shrink-0 mt-0.5" /><span>{f}</span></li>
                ))}
              </ul>
              <Link to="/signup"><Button className="w-full h-11 bg-[#00A651] hover:bg-[#008a43] text-white">Get Started</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 lg:py-28 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Badge className="bg-[#00A651]/10 text-[#00A651] hover:bg-[#00A651]/10 border-0 mb-4">CUSTOMERS</Badge>
          <h2 className="text-3xl lg:text-5xl font-extrabold text-[#0B2545] font-[Poppins] mb-4">Trusted by transporters across Bharat</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 hover-lift">
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 text-[#FFB800] fill-[#FFB800]" />)}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">“{t.quote}”</p>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className="w-11 h-11 rounded-full brand-gradient flex items-center justify-center text-white font-bold">{t.avatar}</div>
                <div>
                  <p className="font-semibold text-[#0B2545]">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.role} — {t.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="bg-[#0B2545] rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#00A651]/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#FFB800]/10 rounded-full blur-3xl"></div>
          <div className="relative">
            <h2 className="text-3xl lg:text-5xl font-extrabold text-white font-[Poppins] mb-4">Ready to move India's freight?</h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">Join 50,000+ transporters. Start your 1-month free trial today. ₹500 + GST/month after that.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/signup"><Button size="lg" className="bg-[#00A651] hover:bg-[#008a43] text-white h-12 px-8 text-base">Start Free Trial <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="h-12 px-8 text-base bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">Sign in</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-10 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-xl text-[#0B2545] font-[Poppins]">EZLOAD</span>
            </div>
            <p className="text-sm text-gray-500">India's digital freight marketplace. Move loads, find trucks, get paid — all in one app.</p>
          </div>
          <div>
            <p className="font-semibold text-[#0B2545] mb-3">Product</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>Load Board</li><li>Truck Board</li><li>e-LR & e-Way Bill</li><li>Payments</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-[#0B2545] mb-3">Company</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>About</li><li>Careers</li><li>Blog</li><li>Contact</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-[#0B2545] mb-3">Legal</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>Terms of Service</li><li>Privacy Policy</li><li>GST & Refund</li><li>Grievance</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-100 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-gray-500">© 2025 EZLOAD Technologies Pvt. Ltd. All rights reserved. GSTIN: 27AABCE1234F1Z5</p>
          <p className="text-xs text-gray-500">Made in India for Bharat</p>
        </div>
      </footer>
    </div>
  );
}
